import './style.scss';

import _ from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Dimmer, DimmerDimmable, Header, Icon } from 'semantic-ui-react';

import { AcquisitionConstantsList } from 'app/utils/acquisitionConstants';
import { ANATOMIC_REGION_CASCADER_TREE, localizeCascaderTreeData } from 'app/utils/xrayRegions';
import { useAcquisitionConstants } from './AcquisitionConstantsContext';
import validateAcquisitionConstants from 'app/components/XRayGeneratorAcquisitionConstantsConfiguration/validateAcquisitionConstants';
import EditableAcquisitionConstantsTable from 'app/components/XRayGeneratorAcquisitionConstantsConfiguration/EditableAcquisitionConstantsTable';
import ColorCodedMultiCascader from 'app/components/ColorCodedMultiCascader';
import { MultiCascader } from 'rsuite';

export default function XRayGeneratorAcquisitionConstantsConfigurationByProfile() {
  const intl = useIntl();
  const { acquisitionConstantsByProfile, updateAcquisitionConstant } = useAcquisitionConstants();
  const [anatomicRegions, setAnatomicRegions] = React.useState([]);
  const [currentAcquisitionConstants, setCurrentAcquisitionConstants] = React.useState(undefined);
  const defaultAcquisitionConstants = acquisitionConstantsByProfile.default;

  const validityByProfile: Record<string, boolean> = useMemo(() => {
    const result: Record<string, boolean> = {};
    for (const [region, constants] of Object.entries(acquisitionConstantsByProfile)) {
      result[region] = _.every(
        constants,
        (constant) => validateAcquisitionConstants(constant) === undefined
      );
    }
    return result;
  }, [acquisitionConstantsByProfile]);

  const handleAnatomicRegionChange: React.ComponentProps<typeof MultiCascader>['onChange'] = (
    values: string[],
    event
  ) => {
    if (values.length === 0) {
      setCurrentAcquisitionConstants(undefined);
      setAnatomicRegions(values);
      return;
    } else if (!currentAcquisitionConstants) {
      for (const value of values) {
        if (value in acquisitionConstantsByProfile) {
          setCurrentAcquisitionConstants(acquisitionConstantsByProfile[value]);
          setAnatomicRegions(values);
          return;
        }
      }
      const value = values[0];
      const acquisitionConstants =
        acquisitionConstantsByProfile[value.split(' ')[0]] ?? acquisitionConstantsByProfile.default;
      setCurrentAcquisitionConstants(acquisitionConstants);
      setAnatomicRegions(values);
    }
    setAnatomicRegions(values);
  };

  const onSave = useCallback(
    (acquisitionConstants: AcquisitionConstantsList) => {
      for (const region of anatomicRegions) {
        updateAcquisitionConstant(region, acquisitionConstants);
      }
      setAnatomicRegions(undefined);
      setCurrentAcquisitionConstants(undefined);
    },
    [anatomicRegions, updateAcquisitionConstant]
  );

  const localizedData = useMemo(
    () => localizeCascaderTreeData(ANATOMIC_REGION_CASCADER_TREE, intl),
    [intl]
  );

  return (
    <div>
      <div>
        <h5>
          <FormattedMessage id="xray_generator.configuration.choose_regions" />
        </h5>
        <br />
        <ColorCodedMultiCascader
          className="xray-generator-anatomic-region-selector"
          columnHeight={-1}
          columnWidth={-1}
          searchable={false}
          data={localizedData}
          value={anatomicRegions}
          onChange={handleAnatomicRegionChange}
          popupStyle={{ zIndex: 1001, textTransform: 'capitalize' }}
          valuesByKey={acquisitionConstantsByProfile}
          isChildKey={(itemValue, key) =>
            (key.startsWith(itemValue) && key !== itemValue) || itemValue === 'default'
          }
          validityByKey={validityByProfile}
          invalidValueIntlKey="xray_area.constant.validation_errors.invalid"
          invalidChildValueIntlKey="xray_area.constant.validation_errors.child_invalid"
        />
      </div>
      <br />
      <DimmerDimmable blurring dimmed={currentAcquisitionConstants === undefined}>
        <Dimmer active={currentAcquisitionConstants === undefined}>
          <Header as="h2" icon inverted>
            <Icon name="lock" />
            <FormattedMessage id="xray_generator.configuration.pick_a_region" />
          </Header>
        </Dimmer>
        <h5>
          <FormattedMessage id="xray_generator.configuration.choose_constantes" />
        </h5>
        <br />
        <EditableAcquisitionConstantsTable
          acquisitionConstants={currentAcquisitionConstants ?? defaultAcquisitionConstants}
          onSave={onSave}
        />
      </DimmerDimmable>
    </div>
  );
}
